import { useEffect, useState } from "react";
import { Download, FileCheck2, FileText, Loader2 } from "lucide-react";
import {
  createProcurementDocumentSnapshot,
  listProcurementDocumentSnapshots,
  type ProcurementDocumentSnapshot,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { qrSvg } from "@/lib/qr";
import { displayDate, money, titleCase } from "@/lib/utils";

type DocumentTarget = {
  id: string;
  requestNo: string;
  title: string;
  status: string;
};

const html = (value: unknown) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const actor = (value: any) =>
  value?.name || value?.full_name || value?.email || "Recorded actor";

const verificationUrl = (snapshot: ProcurementDocumentSnapshot) =>
  `${window.location.origin}/verify/${snapshot.verification_code}`;

function fileNoteHtml(snapshot: ProcurementDocumentSnapshot) {
  const p = snapshot.payload || {};
  const org = p.organisation || {};
  const pc = p.case || {};
  const req = p.requisition || {};
  const approvals = Array.isArray(p.approvals) ? p.approvals : [];
  const orders = Array.isArray(p.orders) ? p.orders : [];
  const receipts = Array.isArray(p.receipts) ? p.receipts : [];
  const fulfilment = Array.isArray(p.fulfilment) ? p.fulfilment : [];
  const invoices = Array.isArray(p.invoices) ? p.invoices : [];
  const timeline = Array.isArray(p.timeline) ? p.timeline : [];
  const order = orders[0] || {};
  const receipt = receipts[0] || {};
  const invoice = invoices[0] || {};
  const verify = verificationUrl(snapshot);
  const approvalsLabel =
    approvals.map((row: any) => actor(row.actor)).join(", ") ||
    "Not yet recorded";
  const operationsLabel =
    fulfilment
      .map((row: any) => row.details?.performed_by || actor(row.recorder))
      .filter(Boolean)
      .join(", ") || "Not yet recorded";
  const rows =
    timeline
      .slice(-6)
      .map(
        (row: any) =>
          `<tr><td>${html(titleCase(row.action || "event"))}</td><td>${html(
            actor(row.actor),
          )}${row.reason ? ` · ${html(row.reason)}` : ""}</td><td>${html(
            displayDate(row.created_at, true),
          )}</td></tr>`,
      )
      .join("") || '<tr><td colspan="3">No lifecycle evidence recorded.</td></tr>';

  return `<!doctype html><html lang="en"><head><meta charset="utf-8">
<title>Procurement File Note · ${html(req.request_no || pc.case_reference)}</title>
<style>
@page{size:A4;margin:9mm}*{box-sizing:border-box}body{margin:0;color:#102733;font:9pt/1.32 Arial,sans-serif}
.sheet{min-height:278mm;border:1px solid #cad7d2;padding:7mm;position:relative}
header{display:grid;grid-template-columns:1fr 34mm;gap:7mm;border-bottom:2px solid #0f766e;padding-bottom:4mm}
.brand{font-weight:900;letter-spacing:.22em;color:#0f766e}h1{font:700 22pt/1.05 Georgia,serif;margin:2.5mm 0 1mm}
.sub{color:#607278}.seal{text-align:right}.seal svg{width:27mm;height:27mm}.seal small{display:block;color:#607278;font-size:6.5pt}
.grid{display:grid;grid-template-columns:1fr 1fr;gap:3.5mm;margin-top:3.5mm}
section{border:1px solid #dfe7e3;border-radius:2.5mm;padding:3.5mm;break-inside:avoid}.full{grid-column:1/-1}
h2{margin:0 0 2mm;color:#0f766e;font-size:7.5pt;letter-spacing:.12em;text-transform:uppercase}
dl{display:grid;grid-template-columns:29mm 1fr;gap:1.1mm 2.5mm;margin:0}dt{color:#607278}dd{margin:0;font-weight:600}
table{width:100%;border-collapse:collapse}th,td{border-bottom:1px solid #e5ebe8;padding:1.3mm .8mm;text-align:left}
th{color:#607278;font-size:6.7pt;text-transform:uppercase}.hash{margin-top:3.5mm;padding:2.5mm;background:#f2f7f5;border-radius:2mm;font:6.6pt/1.35 monospace;overflow-wrap:anywhere}
footer{position:absolute;bottom:5mm;left:7mm;right:7mm;display:flex;justify-content:space-between;color:#718389;font-size:6.6pt}
.controls{position:fixed;right:12px;top:12px;z-index:5}.controls button{border:0;border-radius:8px;background:#0f766e;color:#fff;padding:10px 14px;font-weight:700}
@media print{.controls{display:none}.sheet{border:0}}
</style></head><body>
<div class="controls"><button onclick="window.print()">Print / save PDF</button></div>
<article class="sheet"><header><div><div class="brand">RAQOZ</div><h1>Procurement File Note</h1>
<div class="sub">${html(org.legal_name || org.name)} · ${html(req.request_no || pc.case_reference)}</div></div>
<div class="seal">${qrSvg(verify)}<small>Scan to verify</small></div></header>
<div class="grid">
<section><h2>Purchase purpose</h2><dl>
<dt>Requirement</dt><dd>${html(req.title || "—")}</dd>
<dt>Description</dt><dd>${html(req.description || "—")}</dd>
<dt>Needed by</dt><dd>${html(req.needed_by || "—")}</dd>
<dt>Estimated</dt><dd>${html(money(Number(req.estimated_value || 0), order.currency || "INR"))}</dd>
<dt>State</dt><dd>${html(titleCase(req.status || pc.operational_status || "recorded"))}</dd>
</dl></section>
<section><h2>Accountability</h2><dl>
<dt>Requestor</dt><dd>${html(actor(req.requestor))}</dd>
<dt>Approver(s)</dt><dd>${html(approvalsLabel)}</dd>
<dt>Procurement</dt><dd>${html(actor(order.creator))}</dd>
<dt>Receiver</dt><dd>${html(actor(receipt.receiver))}</dd>
<dt>In-charge</dt><dd>${html(operationsLabel)}</dd>
</dl></section>
<section><h2>Order and supplier</h2><dl>
<dt>PO</dt><dd>${html(order.po_no || "Not yet issued")}</dd>
<dt>Supplier</dt><dd>${html(order.vendor?.name || "Not yet awarded")}</dd>
<dt>Order value</dt><dd>${html(order.total ? money(Number(order.total), order.currency) : "—")}</dd>
<dt>Status</dt><dd>${html(titleCase(order.status || "not issued"))}</dd>
<dt>Issued</dt><dd>${html(order.issued_at ? displayDate(order.issued_at, true) : "—")}</dd>
</dl></section>
<section><h2>Receipt, outcome and invoice</h2><dl>
<dt>GRN</dt><dd>${html(receipt.grn_no || "Not yet recorded")}</dd>
<dt>Received</dt><dd>${html(receipt.received_at ? displayDate(receipt.received_at, true) : "—")}</dd>
<dt>Ops evidence</dt><dd>${html(fulfilment.length)}</dd>
<dt>Invoice</dt><dd>${html(invoice.invoice_no || "Not yet recorded")}</dd>
<dt>Invoice state</dt><dd>${html(titleCase(invoice.status || "not recorded"))}</dd>
</dl></section>
<section class="full"><h2>Latest decision and evidence events</h2><table>
<thead><tr><th>Stage</th><th>Attributable evidence</th><th>When</th></tr></thead><tbody>${rows}</tbody>
</table></section></div>
<div class="hash"><strong>Immutable snapshot · Version ${snapshot.version}</strong><br>SHA-256: ${html(snapshot.sha256)}<br>Verification: ${html(verify)}</div>
<footer><span>Sealed ${html(displayDate(snapshot.generated_at, true))}</span><span>${html(snapshot.sha256.slice(0,16))}…</span></footer>
</article></body></html>`;
}

function auditPack(snapshot: ProcurementDocumentSnapshot) {
  const body = JSON.stringify(
    {
      manifest: {
        document_id: snapshot.id,
        document_kind: snapshot.document_kind,
        version: snapshot.version,
        generated_at: snapshot.generated_at,
        sha256: snapshot.sha256,
        verification_url: verificationUrl(snapshot),
      },
      payload: snapshot.payload,
    },
    null,
    2,
  );
  const url = URL.createObjectURL(
    new Blob([body], { type: "application/json" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = `RAQOZ-audit-pack-${snapshot.procurement_case_id}-v${snapshot.version}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

export function ProcurementDocumentActions({
  organisationId,
  journey,
}: {
  organisationId: string;
  journey: DocumentTarget;
}) {
  const [busy, setBusy] = useState<"" | "file_note" | "audit_pack">("");
  const [error, setError] = useState("");
  const [snapshots, setSnapshots] = useState<ProcurementDocumentSnapshot[]>([]);

  useEffect(() => {
    let live = true;
    listProcurementDocumentSnapshots(organisationId, journey.id)
      .then((rows) => live && setSnapshots(rows))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [organisationId, journey.id]);

  async function generate(kind: "file_note" | "audit_pack") {
    setBusy(kind);
    setError("");
    const preview = kind === "file_note" ? window.open("", "_blank") : null;
    if (preview)
      preview.document.write(
        "<p style='font-family:Arial;padding:2rem'>Sealing RAQOZ procurement snapshot…</p>",
      );
    try {
      const snapshot = await createProcurementDocumentSnapshot(
        organisationId,
        journey.id,
        kind,
      );
      setSnapshots((current) => [snapshot, ...current]);
      if (kind === "file_note") {
        if (!preview)
          throw new Error("Allow pop-ups to open the printable file note.");
        preview.opener = null;
        preview.document.open();
        preview.document.write(fileNoteHtml(snapshot));
        preview.document.close();
      } else auditPack(snapshot);
    } catch (reason) {
      preview?.close();
      setError((reason as Error).message);
    } finally {
      setBusy("");
    }
  }

  const latest = snapshots[0];
  return (
    <div className="flex flex-col items-start gap-2 sm:items-end">
      <div className="flex flex-wrap gap-2">
        <Button disabled={Boolean(busy)} onClick={() => void generate("file_note")} size="sm" variant="outline">
          {busy === "file_note" ? <Loader2 className="size-4 animate-spin" /> : <FileText className="size-4" />}
          File note PDF
        </Button>
        <Button disabled={Boolean(busy)} onClick={() => void generate("audit_pack")} size="sm" variant="outline">
          {busy === "audit_pack" ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
          Audit pack
        </Button>
      </div>
      {latest && <span className="inline-flex items-center gap-1 text-[11px] text-[var(--text-muted)]"><FileCheck2 className="size-3" />Latest sealed v{latest.version} · {latest.sha256.slice(0, 12)}…</span>}
      {error && <span className="max-w-sm text-xs text-[var(--error)]" role="alert">{error}</span>}
    </div>
  );
}
